import { List } from "immutable";
import { isNil } from "lodash";

import { DateFormatIdentifiers } from "../../constants/dateFormats";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import syncApplications from "../../constants/syncApplications";
import SyncCustomFieldOptions from "../../constants/SyncCustomFieldOptions";
import syncDocumentTypes from "../../constants/syncDocumentTypes";
import syncFieldTypes from "../../constants/syncFieldTypes";
import syncLocalFields from "../../constants/syncLocalFields";
import {
  scrivenerStringFromEndDate,
  stringFromDate,
  stringFromEndDate
} from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import typeSelectors from "../../modules/definition/types/selectors";
import syncSettingsSelectors from "../../modules/sync/settings/selectors";
import immutableRecords from "../../types/immutableRecords";
import { indexListBy } from "../../utils/immutableExtensions";
import getFieldNameForId from "./getFieldNameForId";

const scrivenerDateFieldSyncOptions = {
  dateFormat: DateFormatIdentifiers.Custom,
  customFormat: "%F-%m-%d %H:%M:%S"
};

const dateOptionsForFieldType = fieldType => (fieldType === syncFieldTypes.DATE ? scrivenerDateFieldSyncOptions : {});

const stringForPropertyValue = (prop, value) => {
  if (prop.format === PropertyFormatIdentifier.boolean) {
    return value ? "Yes" : "No";
  } else if (prop.format === PropertyFormatIdentifier.list) {
    // Need to convert from ID to text value
    const option = prop.allowed.find(a => a.id === value);
    return option ? option.label : null;
  }
  return String(value);
};

const stringForReference = (item, reference, itemsById, relationshipsById, typesById) => {
  const entityTypesById = typesById.filter(type => typeSelectors.isSyncToEntityType(type));
  const forwardRefs = item.references
    .map(relId => {
      const rel = relationshipsById.get(relId);
      if (rel.reference === reference.id) {
        const objId = rel.object;
        const obj = itemsById.get(objId);
        if (entityTypesById.has(obj.type)) {
          return obj.label;
        }
      }
      return null;
    })
    .filter(id => id);

  const inverseRefs = reference.bidirectional
    ? item.inverseReferences
      .map(relId => {
        const rel = relationshipsById.get(relId);
        if (rel.reference === reference.id) {
          const objId = rel.subject;
          const obj = itemsById.get(objId);
          if (entityTypesById.has(obj.type)) {
            return obj.label;
          }
        }
        return null;
      })
      .filter(id => id)
    : List();

  return forwardRefs.concat(inverseRefs).join("\n");
};

const getStartDateString = (calendar, date, customFieldType) => {
  return date && !isNil(date.timestamp)
    ? stringFromDate(calendar, date, dateOptionsForFieldType(customFieldType))
    : null;
};

const getEndDateString = (calendar, endDate, startDate, customFieldType) => {
  if (customFieldType === syncFieldTypes.DATE) {
    if (!endDate || isNil(endDate.timestamp)) {
      return null;
    }
    // Scrivener doesn't follow our rules with end dates, because it does not support 24:00:00
    // So instead, we need to adjust the end date by a second here if it doesn't match start and
    // falls on 00:00:00
    return scrivenerStringFromEndDate(calendar, endDate, startDate, dateOptionsForFieldType(customFieldType));
  }
  const endDateStr = endDate && !isNil(endDate.timestamp) ? stringFromEndDate(calendar, endDate, startDate) : null;
  return endDateStr ? endDateStr.replace("24:00:00", "23:59:59") : endDateStr;
};

const isGuaranteedField = fieldId =>
  fieldId === syncLocalFields.label || fieldId === syncLocalFields.summary || fieldId === syncLocalFields.color;
const isDateField = fieldId => fieldId === syncLocalFields.start || fieldId === syncLocalFields.end;

const isAllowedField = (itemType, fieldId, syncingApp) => {
  if (syncingApp == syncApplications.ULYSSES && itemType.isNarrativeFolder) {
    return fieldId === syncLocalFields.label;
  }
  return (
    isGuaranteedField(fieldId) ||
    (isDateField(fieldId) && itemType.canHaveDate) ||
    itemType.properties.indexOf(fieldId) >= 0 ||
    itemType.references.indexOf(fieldId) >= 0
  );
};

const syncDocumentFromItem = (
  item,
  externalId,
  documentOrFolder,
  syncSettings,
  itemsById,
  typesById,
  relationshipsById,
  propertiesById,
  referencesById,
  colorsById,
  tagsById,
  calendar,
  externalCustomFields
) => {
  const itemType = typesById.get(item.type);
  const keywordTypeIds = syncSettingsSelectors.getSyncKeywordTypesFromSettings(syncSettings);
  const referenceKeywords = item.references
    .map(relId => {
      const rel = relationshipsById.get(relId);
      const objId = rel.object;
      const obj = itemsById.get(objId);
      if (keywordTypeIds.contains(obj.type)) {
        return obj.label;
      }
      return null;
    })
    .filter(id => id)
    .toList();

  const inverseReferenceKeywords = item.inverseReferences
    .map(relId => {
      const rel = relationshipsById.get(relId);
      const objId = rel.subject;
      const obj = itemsById.get(objId);
      if (keywordTypeIds.contains(obj.type)) {
        return obj.label;
      }
      return null;
    })
    .filter(id => id)
    .toList();

  const tagKeywords = item.tags.map(tagId => tagsById.get(tagId)).toList();

  // Combine into one list, turning into Set and back to force uniqueness
  // If this is a folder, then no keywords are allowed for Ulysses
  const keywords =
    syncSettings.app == syncApplications.ULYSSES && itemType.isNarrativeFolder
      ? List()
      : referenceKeywords
        .concat(inverseReferenceKeywords)
        .concat(tagKeywords)
        .toSet()
        .toList();

  // We filter out any values that are not allowed for that item due to its type.
  // That way, a null value in the properties map for a key means value is allowed, but
  // nothing is set.
  // Whereas the key being missing altogether means the value is not allowed.
  let properties = indexListBy(
    syncSettings.fields
      .filter(f => f.customFieldId && f.customFieldId !== SyncCustomFieldOptions.NONE)
      .map(f => {
        if (f.customFieldId === SyncCustomFieldOptions.ULYSSES_NOTE) {
          return f.set("customFieldId", getFieldNameForId(propertiesById, referencesById, f.id));
        }
        return f;
      }),
    "customFieldId"
  );

  properties = properties
    .filter(f => isAllowedField(itemType, f.id, syncSettings.app))
    .map(f => {
      switch (f.id) {
        case syncLocalFields.label:
          return item.label;
        case syncLocalFields.summary:
          return item.summary;
        case syncLocalFields.color: {
          return item.colorId && colorsById.has(item.colorId) ? colorsById.get(item.colorId).label : null;
        }
        case syncLocalFields.start: {
          const field = externalCustomFields.get(f.customFieldId);
          const fieldType = field
            ? field.type
            : syncSettings.app === syncApplications.SCRIVENER
              ? syncFieldTypes.DATE
              : syncFieldTypes.TEXT;
          return getStartDateString(calendar, item.preferredStartDate, fieldType);
        }
        case syncLocalFields.end: {
          const field = externalCustomFields.get(f.customFieldId);
          const fieldType = field
            ? field.type
            : syncSettings.app === syncApplications.SCRIVENER
              ? syncFieldTypes.DATE
              : syncFieldTypes.TEXT;
          return item.ongoing
            ? null
            : getEndDateString(calendar, item.preferredEndDate, item.preferredStartDate, fieldType);
        }
        default:
          break;
      }
      // See if it matches a property
      if (item.propertyValues.has(f.id)) {
        return stringForPropertyValue(propertiesById.get(f.id), item.propertyValues.get(f.id));
      }
      // Otherwise, see if it matches a reference type
      if (referencesById.has(f.id)) {
        return stringForReference(item, referencesById.get(f.id), itemsById, relationshipsById, typesById);
      }
      return null;
    });

  const documentType = isNil(documentOrFolder)
    ? itemType.isNarrativeFolder
      ? syncDocumentTypes.FOLDER
      : syncDocumentTypes.TEXT
    : documentOrFolder;

  return immutableRecords.SyncDocumentRecord({
    id: externalId,
    itemType: item.type,
    type: documentType,
    label: item.label,
    properties,
    keywords
  });
};

export default syncDocumentFromItem;
